// Industry state, stored as sparse maps keyed by packed square, alongside the
// chess board (board.ts). Same keys as Position.board.
//
// These were 0x88-indexed typed arrays: 128 slots per field, which was the
// whole board. Two things killed that. The board expands, so there is no fixed
// size to allocate; and a square is a *packed coordinate* now, so the key
// space is about a million wide however small the board is — a dense array
// would be a million slots to hold a dozen belts.
//
// Every field is read through an accessor with a default (`heapAt`, `beltDirAt`
// ...) and written through one that deletes when the value returns to that
// default, so an empty square costs nothing and iteration only ever visits
// live cells. Reading a Map directly gets `undefined` for an empty square,
// which is the bug the accessors exist to prevent — `heap[sq] += n` used to be
// safe and would now be NaN.

import {
  ALTAR,
  ARMORY,
  BATTERY,
  CABLING_TOWER,
  COAL_GENERATOR,
  FIRM_WALL,
  NUCLEAR_GENERATOR,
  REINFORCED_WALL,
  SIMPLE_WALL,
  GRADE_SLOTS,
  KEYSTONE,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  makeUnit,
  RELIC,
  STORE,
  type StructureKind,
  TURRET,
  unitGrade,
  unitLine,
  unitTouched,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
} from "src/shared/industry";
import {
  type Color,
  DIR_OFFSET,
  type Direction,
  parseSquare
} from "../board";
import { type Grade, RULES, inventoryOf } from "../rules";
import {
  cobbleShare,
  DEPOSIT_TIERS,
  depositName,
  isLadder,
  type MaterialLine,
  materialValue,
  METAL,
  PURE,
  STONE
} from "../materials";
// Types and two lookups only — `position.ts` does not import this module,
// so this direction is safe.
import { hasCell, type Position } from "../position";

// Direction is chess vocabulary now (board.ts) because pawn facing decides
// movement; re-exported here so industry callers have one import site.
export type { Direction } from "../board";
export {
  DIRECTIONS,
  DIRECTION_NAMES,
  DIR_OFFSET,
  EAST,
  NORTH,
  SOUTH,
  WEST
} from "../board";

export { GRADE_SLOTS };
export const NO_BELT = -1;
export const NO_CARGO = -1;


export interface IndustryState {
  /** Scrap remaining on each square; absent = plain board. */
  /**
   * Ground *arriving*, as square -> how much further it will carry.
   *
   * A seed is placed at the edge nearest your army
   * and each turn every seed pushes its **cardinal** neighbours onto the board,
   * handing each one its own counter minus one — so the growth radiates out
   * and runs down, reaching exactly as far as the number it started with.
   *
   * Cardinal and not eight-way, deliberately: new ground is built rather than
   * blown — a four-way push grows lobes and inlets, which is the ragged shape
   * `FORMATIONS` already argues a growing board wants. Eight-way would pour out
   * a rectangle.
   */
  growth: Map<number, number>;
  heap: Map<number, number>;
  /**
   * Whose piece the scrap on this square used to be, and which piece.
   *
   * Scrap is a *corpse*, and drawing it as an anonymous cone threw away the
   * one piece of information a player most wants from a glance across the
   * board: what died here, and whose it was. Two fields rather than one packed
   * word because they are read separately and by different things — the colour
   * tints the wreck, the rank chooses its shape.
   *
   * A square that takes a second kill keeps the *higher* rank. That is a
   * choice and not an accident: the pile is drawn as one wreck, the biggest
   * thing in it is what the pile is about, and "whatever died most recently"
   * would make a heap flicker between shapes as an exchange resolves on it.
   */
  heapSide: Map<number, number>;
  heapRank: Map<number, number>;
  /**
   * Ore **in the ground**: how many units are left in this tile of a field,
   * and what one of them is (§26 F3).
   *
   * Its own store rather than a flag on `heap`, and the difference is what a
   * deposit *is*. A heap is a corpse — it lies on the square, a queen sweeps
   * it up, a belt may not be laid over it, and what it leaves is scrap. A
   * deposit is terrain: nothing sweeps it, track runs over it, and it is dug
   * a tile at a time until that tile is dry. Sharing the heap map made every
   * one of those the wrong answer by default.
   *
   * A **field** is many of these — a patch of a dozen-odd contiguous tiles,
   * all one line, each with its own finite charge — so a pawn drills the tile
   * it stands on and then steps to the next one.
   *
   * `depositYield` is **required**, not defaulted: a heap has an obvious
   * material and a deposit does not, so a tile with a count and no yield is a
   * bug rather than a plain iron seam. The two are written together by
   * `setDeposit` and cleared together by `clearDeposit`, which is the one
   * choke point every square-emptying path goes through.
   */
  deposit: Map<number, number>;
  depositYield: Map<number, number>;
  /**
   * **A mixed tile** (§31 D1): its tier, and how many of `deposit`'s units are
   * cobble — the rest are `depositYield`. Absent = a pure tile (a quarry, or
   * an authored one). Written by `setDeposit`, cleared by `clearDeposit`.
   */
  depositTier: Map<number, number>;
  depositCobble: Map<number, number>;
  /**
   * Turns spent digging the special in this tile. Absent = none.
   *
   * A counter rather than a stamp, unlike `minedAt`: what it measures is
   * *cumulative* work on one tile across turns, which a "when last" cannot
   * say. It goes when the tile does, so it can never outlive its deposit.
   */
  digProgress: Map<number, number>;
  /**
   * The zone each square lies in (§31 D2), `src/shared/zones.ts` kinds.
   * Terrain, like a deposit: a label nothing in the rules reads yet. Absent
   * on every board without zones, so an older log, save and hash is unmoved.
   */
  zone?: Map<number, number>;
  /**
   * Equipment (§31 A0): artifact gear, held by a Monarch or lying on a square.
   * A list, not a square-keyed Map — held gear travels with its body by
   * `pieceId`, written by `syncGear` off a diff in `applyCommand`. Absent = none.
   */
  gear?: readonly Gear[];

  /** Belt part per square: direction, owner, on/off. */
  beltDir: Map<number, Direction>;
  beltOwner: Map<number, Color>;
  beltOn: Map<number, number>;

  /**
   * Runs that have been *drawn* but not yet paid for: direction and owner.
   *
   * A part costs one unit of Iron (`RULES.beltCost`), and the two halves of
   * that are separated in time on purpose. Laying is free of tempo, so a run
   * is drawn without spending a move on your own turn; the metal arrives in the
   * industrial phase, one part at a time, through the same network everything
   * else moves through. What that buys is a network that has to be *supplied*
   * rather than merely afforded — a lane to a far corner is not gated on a
   * balance, it is gated on getting iron out there.
   *
   * **A blueprint is not a belt.** It carries nothing, nothing rides it, it is
   * not live and it is not in `liveBelts`. It is a machine with one recipe.
   * Its own maps rather than a flag beside `beltDir` for exactly that reason:
   * every question in the game asks `hasBelt`, and a belt that is not a belt
   * would have to be excluded at each one, by hand and in silence.
   */
  blueprintDir: Map<number, Direction>;
  blueprintOwner: Map<number, Color>;

  /**
   * The three material stores: cargo riding belt parts, a machine's input
   * bank, and a refinery's output bank. One array of packed units per live
   * square — the parallel `*Len` arrays are gone, because an array knows its
   * own length. See the material section below for the packing and for why
   * these are units rather than per-grade counts.
   */
  beltCargo: Map<number, number[]>;
  buffer: Map<number, number[]>;
  /**
   * Refinery *output*, kept separate from input on purpose. Sharing one buffer
   * lets the oven pick its own finished material back up and cook it again —
   * an infinite upgrade loop that quietly eats the input. This is why PLAN
   * §3.2 specifies "Buffers 2 / 2" rather than one number.
   */
  outBuffer: Map<number, number[]>;
  /**
   * Refinery oven: the packed unit it will emit next tick. Absence means the
   * oven is empty — which is why this is a Map and not a number with a
   * sentinel, since a packed unit can legitimately be 0 (grade 0, no
   * provenance) and any in-band "empty" value would be ambiguous.
   */
  cooking: Map<number, number[]>;
  /** Foundry target piece type per rook square; absent = unset. */
  target: Map<number, number>;
  /**
   * Facing a foundry stamps into the pawns it mints, +1 so that 0 can mean
   * "unset" and fall back to the side's forward. Industry state, not chess
   * state: it is a machine setting, not a property of any piece on the board.
   */
  mintFacing: Map<number, number>;

  /**
   * Stable identity for each refinery, 1-based; absent means "not yet
   * assigned". Travels with the piece through `migratePiece`, because the rule
   * it serves — a refinery may not process the same unit twice — is about the
   * piece, not the square it happens to be standing on.
   */
  refinerId: Map<number, number>;
  /** Next identity to hand out. In state so replays assign the same ones. */
  nextRefinerId: number;

  /**
   * A miner works at a fixed rate: one dig and one hand-off per turn, whether
   * by the automatic pass or by the player's button.
   *
   * Recorded as "the turn counter when this square last did it" rather than a
   * flag that something has to remember to clear — a reset loop is exactly the
   * kind of bookkeeping that gets missed on one code path and silently grants
   * a free extra dig. `turnCounter` ticks once per tick (§31 T: two or more a turn).
   */
  /**
   * A hand-off direction chosen by the player, +1 so 0 can mean "unset".
   *
   * Routing is automatic — material walks toward the nearest machine that can
   * use it — and that is right nearly always and wrong exactly when the player
   * can see something the gradient cannot: a lane being built, a foundry being
   * saved for. This is the override, and it belongs to the *piece*, so it
   * travels with it through `migratePiece`.
   */
  handoff: Map<number, number>;
  /**
   * Whether a piece sits on what it holds or passes it along.
   *
   * `HOLD_RETAIN` or `HOLD_PASS`; absent means the piece has never been told,
   * and falls to its type's default. Three states rather than two because a
   * foundry's default is the opposite of everything else's — it stockpiles for
   * its recipe — so "absent" and "told to pass on" have to be different
   * things or a rook's switch would have no off position.
   */
  /**
   * Pieces told to **dump** material they cannot use.
   *
   * One boolean, replacing a three-state retain/pass policy. That policy could
   * say "pass on *everything*", which walked a foundry's recipe savings away
   * one ingot at a time — so the setting that existed to un-stick a full
   * foundry was also the setting that stopped it ever filling. What a player
   * actually wants is "stop hoarding what you have no use for", which is a
   * question about each unit rather than about the piece.
   *
   * Absent means off. Present and 1 means dump.
   */
  dump: Map<number, number>;
  /**
   * What a king is doing: `TASK_MINE`, `TASK_REFINE` or `TASK_FORGE`, +1 so
   * that 0 means "not chosen — work it out from what is to hand".
   *
   * A king does one of the three in a turn, never all three, and holding it
   * to one job is what makes the choice a decision rather than a bonus.
   */
  kingTask: Map<number, number>;
  /**
   * How many consecutive turns a king has stayed on its current job, which is
   * also its capacity for that job this turn. A king that is left to get on
   * with it works faster and faster; switching jobs puts it back to one.
   */
  kingTempo: Map<number, number>;
  /**
   * The job a king actually spent its last turn on, which is what a *switch*
   * is measured against. Separate from `kingTask`, which is the order given:
   * a king with no orders still builds tempo on whatever it keeps finding to
   * do, and one whose orders change mid-streak has to lose the streak.
   */
  kingLastTask: Map<number, number>;
  /** The turn a king last acted, so it cannot act twice in one. */
  kingActedAt: Map<number, number>;
  /**
   * Units lying on a square, waiting to be picked up — a lifted lane's
   * ingots, an altar's overflow. A heap is a *count* and graded material is
   * a *unit*. Sorted on write, like every other ordered thing here, because
   * two clients derive this from the same log.
   */
  dropped: Map<number, number[]>;
  minedAt: Map<number, number>;
  /**
   * The round a king last took a piece apart (Sacrifice) — `Position.round`,
   * one turn per side per round (§31 T).
   *
   * A stamp rather than a flag, the `minedAt` pattern exactly:
   * a reset loop is how a free extra action gets granted the day one code path
   * forgets to clear it. Its own map rather than the king's job flag, because
   * sacrificing is not one of the three jobs a king does a turn — it does not
   * stop the king mining, and mining does not stop it.
   */
  sacrificedAt: Map<number, number>;
  loadedAt: Map<number, number>;
  // **The disguise is `Position`'s now** (§26 C12), for the reason health left
  // in §27: it decides which captures are legal, so movegen has to see it.
  /**
   * Units this piece holds back from the automatic passes. Default 0.
   *
   * A knight cannot repair out of a buffer the carry gradient keeps draining,
   * so the carrier gets one number. Zero is exactly today's behaviour —
   * everything it picks up walks on toward the best valid sink — and a knight
   * nobody has configured is still purely a link in a brigade.
   *
   * **It blocks the automatic passes only.** An explicit hand-off still empties
   * it, exactly as `dump` already works: two rules that behave differently for
   * the same intent is how a player ends up with two mental models.
   */
  reserve: Map<number, number>;
  /**
   * The **round** (`position.fullmove`) a Confessor last empowered somebody.
   * The `minedAt` pattern — a per-piece clock movegen
   * never reads, which `migratePiece` carries so it is hers and not her
   * square's.
   *
   * **A round and not a ply**, which is where `armour` and `disguise` count and
   * is wrong here: a round is `sides` plies only while a turn is one move, and
   * this is the one ability that hands *moves* out. Counting plies had the two
   * moves she gives away tick down her own cooldown, and she came off it on her
   * next turn.
   */
  preachedAt: Map<number, number>;
  /**
   * Fuel points a machine holds, by square (§31 M2) — **a per-machine pool the
   * tick reads**, so a generator network (S6) deposits into the same place.
   * A refiner fills it by burning coal; raising metal a rung spends one; it
   * empties at the start of its owner's turn (`expireFuel`). The piece's, so
   * `migratePiece` carries it.
   */
  fuel: Map<number, number>;
  turnCounter: number;
  /**
   * Buildings, by the square each one stands on (PLAN §14).
   *
   * The third kind of thing on the board: pieces move and fight, belts lie
   * under everything and carry, and a structure stands on a square and takes
   * it **out of `Position.cells`** for as long as it is there. That is the
   * whole encoding — movement already tests cell membership per step, so a
   * square that stops being a cell is already a wall that blocks sliders,
   * cannot be landed on and cannot be castled through, with not one line
   * changed in `src/engine/rules/chess/`.
   *
   * The alternative — an occupant in `position.board` with a type outside
   * `PAWN..KING` — would have ridden the existing occupancy checks just as
   * cheaply and failed *silently* everywhere that switches on piece type:
   * `PIECE_VALUE[7]` is `undefined`, so a building would be worth NaN to the
   * AI, to a badge, to FEN and to `spillPiece`. Silent and wide is the failure
   * mode this codebase has paid for most often.
   */
  structures: Map<number, Structure>;
}

// The kinds of building are `src/shared/industry.ts`, re-exported here.
export {
  ALTAR,
  ARMORY,
  BATTERY,
  CABLING_TOWER,
  COAL_GENERATOR,
  FIRM_WALL,
  KEYSTONE,
  NUCLEAR_GENERATOR,
  REINFORCED_WALL,
  RELIC,
  SIMPLE_WALL,
  STORE,
  type StructureKind,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  TURRET,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
};

/**
 * The artifact structures, in kind order. `WELL` is the Sacred Well (§31 A4),
 * `KEYSTONE` the Mainframe (A7); A8, A9, A6 appended.
 */
export const ARTIFACT_KINDS: readonly StructureKind[] = [
  ALTAR,
  RELIC,
  KEYSTONE,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER
];

export const isArtifactKind = (kind: StructureKind): boolean =>
  ARTIFACT_KINDS.includes(kind);

/**
 * One piece of artifact equipment (§31 A0). `kind` is the artifact structure
 * it pairs with and `anchor` the square that structure stands on; `origin` is
 * the side that placed it. `holder` is the bearer's `pieceId`, 0 when lying
 * on `at`; `side` is the bearer's side, -1 when lying. `at` is refreshed to
 * the bearer's square after every command, so the hash keys it by square.
 */
export interface Gear {
  readonly kind: StructureKind;
  readonly origin: number;
  readonly anchor: number;
  readonly holder: number;
  readonly side: number;
  readonly at: number;
  /**
   * The body (`pieceId`) wearing this gear's armor grant now — the Shield of
   * Defiance's +10 (§31 A12, `settleDefiance`). Absent: none. Hashed.
   */
  readonly granted?: number;
}

export interface Structure {
  kind: StructureKind;
  owner: Color;
  /**
   * What it holds — units, exactly as a piece's buffer holds them, so grade
   * and refinery provenance travel into a warehouse and back out again.
   *
   * Bounded by `inventoryOf` (§33 I2) rather than by `MAX_UNITS`: that number
   * is derived from the *pieces'* buffers and is the cap `pushUnit` applies to
   * the three per-square stores. Adding a structure's capacity to it would
   * quietly raise every belt and every piece to match.
   */
  stock: number[];
  /**
   * Per-grade policy, as bitmasks over grade (PLAN §14.4.2).
   *
   * Two independent switches rather than a tri-state, because all four
   * combinations are things a player wants: accept-only is a stockpile,
   * accept-and-offer is a buffer in the middle of a lane, offer-only is a tap
   * you have opened, and neither is sealed.
   */
  accept: number;
  offer: number;
  /**
   * The grade it was paid for in, so dismantling can pay back at grade rather
   * than handing back iron for diamond.
   */
  paidGrade: number;
  /**
   * Health left, from `STRUCTURE_HEALTH` when it went up (§27 applied to F5).
   *
   * **The truth; `Position.structureHealth` is the mirror**, exactly the split
   * the owner already takes — move generation has to know whether a blow
   * finishes a building, and it is handed a `Position` and nothing else, while
   * this is what survives a save and what the hash covers.
   *
   * Optional because the codec is structural: a run saved before buildings
   * could be hurt decodes without it, and an undamaged building is the honest
   * reading of that. `structureHealthOf` is the one place that fills it in.
   */
  health?: number;
  /**
   * A turret's round (§31 S8), absent on every other kind: shots left until
   * its owner's next turn starts, fuel points banked for them, its facing
   * (the cone guns), and whether it stands on a Reinforced Wall. Hashed.
   */
  turret?: TurretState;
  /**
   * An Industrial Battery's stored fuel points (§31 S6) — kept across turns,
   * never expired, capped at `POWER.batteryCapacity`. Absent is empty.
   */
  charge?: number;
  /**
   * A Shield Generator's fuel points taken this owner turn (§31 A9), toward
   * `DEFLECTOR.fuelPerRound`. Settled into `lit` at the owner's End Turn.
   */
  fed?: number;
  /**
   * Extra moves this fuel source's Empower network granted this owner turn
   * (§33 S1), reset at its turn start. Absent is none.
   */
  boosts?: number;
  /** A Shield Generator fed its round's points at its owner's last End Turn (§31 A9). */
  lit?: boolean;
  /**
   * Max HP Yield Not adds while its owner holds the power (§31 A12): 3 on an
   * obstruction, 1 on anything else. Written by `settleDefiance`; hashed.
   */
  bulwark?: number;
  /**
   * A House of Cards' own-side factory ticks toward its next card reward
   * (§31 A11), counted while powered; paid at `ARTIFACTS_LATE.houseTicks`.
   */
  dealt?: number;
  /**
   * A Train Engine's or Car's state (§31 S7), absent on every other kind.
   * Copied deep by `cloneIndustry`; hashed.
   */
  train?: Train;
}

/**
 * A piece riding in a Train Car (§31 S7): its body, off the board, by the
 * fields `shovePiece` carries. Its holdings stayed on the square it boarded
 * from; its machine settings did not travel.
 */
export interface Rider {
  piece: number;
  facing: number;
  hurt: number;
  vitality: number;
  plating: number;
  pieceId: number;
  variant: number;
  imitating: number;
  crowned: number;
}

/**
 * What makes a structure a train (§31 S7). An engine has `heading`, `dest`
 * and `cars`; a car has `riders` and the per-turn stamps. Every train stands
 * on an own Track, which is implied, never stored.
 */
export interface Train {
  /** Engine: the way it last stepped, -1 before its first step. */
  heading?: number;
  /** Engine: the square it is driving to, -1 for none. */
  dest?: number;
  /** Engine: its coupled cars' squares, nearest first. */
  cars?: number[];
  /** Car: who rides in it, in boarding order. */
  riders?: Rider[];
  /** Car: the `Position.round` the counts below belong to. */
  round?: number;
  boarded?: number;
  alighted?: number;
}

/** A deep copy of a train's state. */
export const copyTrain = (train: Train): Train => ({
  ...train,
  ...(train.cars ? { cars: train.cars.slice() } : {}),
  ...(train.riders ? { riders: train.riders.map((one) => ({ ...one })) } : {})
});

/**
 * A turret's state (§31 S8). `shots` refill and `fuel` empties at its
 * owner's turn start (`refreshTurrets`); a shot unspent at End Turn is
 * **overwatch** until then. `facing` aims a Kinetic or Cannon cone.
 */
export interface TurretState {
  shots: number;
  fuel: number;
  facing: number;
  /** Built over an own Reinforced Wall: HP added, ranged-immune, an obstruction. */
  emplaced?: boolean;
}

/** Every grade open. What a new store accepts, so it is useful on arrival. */
export const ACCEPT_ALL = (1 << GRADE_SLOTS) - 1;

export const acceptsGrade = (s: Structure, grade: number): boolean =>
  (s.accept & (1 << grade)) !== 0;
export const offersGrade = (s: Structure, grade: number): boolean =>
  (s.offer & (1 << grade)) !== 0;
/** The mask with one grade switched on or off. */
export const withGrade = (mask: number, grade: number, on: boolean): number =>
  on ? mask | (1 << grade) : mask & ~(1 << grade);

/**
 * A king's jobs. Stored +1, so 0 can mean "decide for me".
 *
 * **Slot 0 is retired, not reused.** It was `TASK_MINE`, and the king does not
 * dig any more — but the value is written into every saved run and into the
 * hash, so renumbering the other two would silently reinterpret a king posted
 * to the ovens as one posted to the forge. Left as a hole, refused on the way
 * in (`set-king-task`) and read as "decide for me" on the way out, which is
 * what a save from before this change should mean anyway.
 */
export const TASK_RETIRED = 0;
export const TASK_REFINE = 1;
export const TASK_FORGE = 2;
export type KingTask = typeof TASK_REFINE | typeof TASK_FORGE;

/** Indexed by task value, so the retired slot keeps its place. */
export const TASK_NAMES = ["", "Refine", "Forge"] as const;

const copyUnits = (src: Map<number, number[]>): Map<number, number[]> => {
  const out = new Map<number, number[]>();
  for (const [sq, units] of src) out.set(sq, units.slice());
  return out;
};

// --- per-square accessors -------------------------------------------------
//
// Read with the `*At` form, write with the `set*` form. The setter deletes on
// the default value, which is what keeps the maps to live cells and what makes
// "iterate everything that has scrap on it" cheap on a big board.

export const heapAt = (I: IndustryState, sq: number): number => I.heap.get(sq) ?? 0;
export function setHeap(I: IndustryState, sq: number, amount: number): void {
  if (amount > 0) {
    I.heap.set(sq, amount);
  } else {
    I.heap.delete(sq);
    // Mined out. The wreck goes with the scrap, or a square that was dug
    // clean keeps drawing a corpse that is not there any more.
    clearHeapMark(I, sq);
  }
}
export const addHeap = (I: IndustryState, sq: number, amount: number): void =>
  setHeap(I, sq, heapAt(I, sq) + amount);

/** Whose wreck this is, or -1 for scrap with no corpse behind it. */
export const heapSideAt = (I: IndustryState, sq: number): number => I.heapSide.get(sq) ?? -1;
/** What died here, or 0. */
export const heapRankAt = (I: IndustryState, sq: number): number => I.heapRank.get(sq) ?? 0;

/**
 * Record what a square's scrap used to be.
 *
 * Keeps the higher rank when a square takes a second kill, so an exchange
 * resolving on one square leaves the wreck of the better piece rather than
 * flickering between the two as each capture lands.
 */
export function markHeap(I: IndustryState, sq: number, side: number, rank: number): void {
  if (rank > heapRankAt(I, sq)) {
    I.heapRank.set(sq, rank);
    I.heapSide.set(sq, side);
  }
}

/** Scrap gone means the wreck is gone with it. */
export function clearHeapMark(I: IndustryState, sq: number): void {
  I.heapSide.delete(sq);
  I.heapRank.delete(sq);
}

/** Units left in this tile of a field. */
export const depositAt = (I: IndustryState, sq: number): number => I.deposit.get(sq) ?? 0;

/** The unit one dig of this tile lifts, or 0 where there is no deposit. */
export const depositYieldAt = (I: IndustryState, sq: number): number =>
  I.depositYield.get(sq) ?? 0;

/** A tile's tier (`PURE` off a mixed tile). */
export const depositTierAt = (I: IndustryState, sq: number): number =>
  I.depositTier.get(sq) ?? PURE;

/** Cobble units left in a mixed tile; nought on a pure one. */
export const depositCobbleAt = (I: IndustryState, sq: number): number =>
  I.depositCobble.get(sq) ?? 0;

/** One cobble, the mixed tile's other half. */
export const COBBLE_UNIT = makeUnit(0, 0, STONE);

/**
 * The unit the next dig of this tile lifts, or -1 where there is none —
 * never 0, which is a unit of iron.
 *
 * **A quota, not a roll** (§31 D1): the named material comes up while its
 * share of what is left is at or above the tier's, cobble otherwise — so a
 * tile's units interleave evenly and the counts the tier promises are exact.
 * No stream, so a dig needs no salt.
 */
export function nextDepositUnit(I: IndustryState, sq: number): number {
  const left = depositAt(I, sq);
  if (left <= 0) return -1;
  const cobble = depositCobbleAt(I, sq);
  if (cobble <= 0) return depositYieldAt(I, sq);
  const other = left - cobble;
  const share = DEPOSIT_TIERS[depositTierAt(I, sq)].other;
  return other > 0 && other * 100 >= share * left ? depositYieldAt(I, sq) : COBBLE_UNIT;
}

/** What is left in a tile, by material: the named one, then cobble. */
export function depositContents(I: IndustryState, sq: number): { unit: number; count: number }[] {
  const left = depositAt(I, sq);
  if (left <= 0) return [];
  const cobble = depositCobbleAt(I, sq);
  const out = [];
  if (left - cobble > 0) out.push({ unit: depositYieldAt(I, sq), count: left - cobble });
  if (cobble > 0) out.push({ unit: COBBLE_UNIT, count: cobble });
  return out;
}

/** "rich iron deposit", "stone quarry" — the tile's name on the fact. */
export function depositNameAt(I: IndustryState, sq: number): string {
  const unit = depositYieldAt(I, sq);
  return depositName(unitLine(unit) as MaterialLine, unitGrade(unit), depositTierAt(I, sq));
}

/** Turns already spent on the special in this tile. */
export const digProgressAt = (I: IndustryState, sq: number): number =>
  I.digProgress.get(sq) ?? 0;

export function setDigProgress(I: IndustryState, sq: number, turns: number): void {
  if (turns > 0) I.digProgress.set(sq, turns);
  else I.digProgress.delete(sq);
}

/**
 * Material a piece standing here could dig — the deposit first, the heap
 * second.
 *
 * One reading, because "is there anything to mine" is asked by the rules, by
 * the planner and by three separate sensors, and a caller that knew about only
 * one of the two stores would walk a pawn past a field or refuse a dig on a
 * corpse. The two coexist only transiently: a corpse dropped on a tile buries
 * it (`wreckPiece`), so in play a square has one or the other.
 */
export const mineableAt = (I: IndustryState, sq: number): number =>
  depositAt(I, sq) + heapAt(I, sq);

/**
 * Every square with something to dig on it, sorted — the union of the two
 * stores, for the sweeps that used to walk `industry.heap` alone.
 *
 * A separate name rather than every caller spreading both maps into
 * `liveSquares`, because the ones that must see a field and the ones that must
 * not are a real distinction: this is "somewhere a pawn could be working", and
 * `industry.heap` on its own now means "somewhere a corpse is lying".
 */
export const mineableSquares = (I: IndustryState): number[] =>
  liveSquares(I.heap, I.deposit);

/**
 * A deposit as a command carries it: square, line, rung, and how much is in
 * the ground. Flat, because that is what a log carries — and a **patch is
 * simply many rows**, which is what keeps the command shape unchanged now
 * that a field is a dozen tiles rather than one.
 */
export type DepositRow = readonly [
  square: number,
  line: number,
  grade: number,
  amount: number,
  /** §31 D1: `PURE` when absent, so an older log replays unchanged. */
  tier?: number
];

/** Put one tile of a field down: a charge, and what a unit of it is. */
export function setDeposit(
  I: IndustryState,
  sq: number,
  line: MaterialLine,
  grade: number,
  amount: number,
  tier: number = PURE
): void {
  if (amount <= 0) {
    clearDeposit(I, sq);
    return;
  }
  I.deposit.set(sq, amount);
  I.depositYield.set(sq, makeUnit(grade, 0, line));
  const cobble = cobbleShare(amount, tier);
  if (tier !== PURE) I.depositTier.set(sq, tier);
  else I.depositTier.delete(sq);
  if (cobble > 0) I.depositCobble.set(sq, cobble);
  else I.depositCobble.delete(sq);
}

/**
 * Take one unit out of this tile, clearing it when it runs dry.
 *
 * **The one choke point**, so a tile can never keep a yield or a half-finished
 * dig it has nothing left to give — the orphan `orphans.test.ts` exists for.
 */
export function drawDeposit(I: IndustryState, sq: number, amount: number): void {
  const left = depositAt(I, sq) - amount;
  if (left > 0) I.deposit.set(sq, left);
  else clearDeposit(I, sq);
}

/**
 * Lift the next unit (`nextDepositUnit`) out of this tile and say what it was
 * (-1 off a deposit).
 * The cobble count goes down with it, so the quota stays exact.
 */
export function liftDeposit(I: IndustryState, sq: number): number {
  const unit = nextDepositUnit(I, sq);
  if (unit < 0) return unit;
  if (unit === COBBLE_UNIT && depositCobbleAt(I, sq) > 0) {
    const cobble = depositCobbleAt(I, sq) - 1;
    if (cobble > 0) I.depositCobble.set(sq, cobble);
    else I.depositCobble.delete(sq);
  }
  drawDeposit(I, sq, 1);
  return unit;
}

/**
 * The tile is gone: its charge, what it was, and any dig part way into it.
 *
 * Called wherever a tile stops being ore — dug dry, buried under a corpse,
 * paved over by a building, or on ground that leaves the board.
 * `removeSquare` gets it free (it sweeps every Map by reflection);
 * `buildStructure` asks for it by name.
 */
export function clearDeposit(I: IndustryState, sq: number): void {
  I.deposit.delete(sq);
  I.depositYield.delete(sq);
  I.depositTier.delete(sq);
  I.depositCobble.delete(sq);
  I.digProgress.delete(sq);
}

export const beltDirAt = (I: IndustryState, sq: number): Direction | -1 =>
  I.beltDir.get(sq) ?? NO_BELT;
export const beltOwnerAt = (I: IndustryState, sq: number): Color | -1 =>
  I.beltOwner.get(sq) ?? -1;
export const beltOnAt = (I: IndustryState, sq: number): number => I.beltOn.get(sq) ?? 0;

/**
 * A part's three fields move together — direction, owner, and whether it is
 * running — so they are written together. Separate setters invited a part that
 * had a direction but no owner, which reads as "somebody's belt" to everything
 * that checks ownership and as nobody's to everything that checks the owner.
 */
export function setBelt(
  I: IndustryState,
  sq: number,
  dir: Direction,
  owner: Color,
  on = 1
): void {
  I.beltDir.set(sq, dir);
  I.beltOwner.set(sq, owner);
  I.beltOn.set(sq, on);
}

export function clearBelt(I: IndustryState, sq: number): void {
  I.beltDir.delete(sq);
  I.beltOwner.delete(sq);
  I.beltOn.delete(sq);
}

export function setBeltDir(I: IndustryState, sq: number, dir: Direction): void {
  if (I.beltDir.has(sq)) I.beltDir.set(sq, dir);
}

export function setBeltOn(I: IndustryState, sq: number, on: number): void {
  if (I.beltDir.has(sq)) I.beltOn.set(sq, on);
}

// --- blueprints -----------------------------------------------------------
//
// A drawn part waiting for its iron. Deliberately the same shape as the belt
// accessors above, minus the switch: an unfunded part has nothing to switch.

export const hasBlueprint = (I: IndustryState, sq: number): boolean =>
  I.blueprintDir.has(sq);

export const blueprintDirAt = (I: IndustryState, sq: number): Direction | -1 =>
  I.blueprintDir.get(sq) ?? NO_BELT;
export const blueprintOwnerAt = (I: IndustryState, sq: number): Color | -1 =>
  I.blueprintOwner.get(sq) ?? -1;

/** Direction and owner together, for the reason `setBelt` writes three. */
export function setBlueprint(
  I: IndustryState,
  sq: number,
  dir: Direction,
  owner: Color
): void {
  I.blueprintDir.set(sq, dir);
  I.blueprintOwner.set(sq, owner);
}

export function clearBlueprint(I: IndustryState, sq: number): void {
  I.blueprintDir.delete(sq);
  I.blueprintOwner.delete(sq);
}

export function setBlueprintDir(I: IndustryState, sq: number, dir: Direction): void {
  if (I.blueprintDir.has(sq)) I.blueprintDir.set(sq, dir);
}

/** The unit nearest to being done — what the badge and the model both show. */
export const cookingAt = (I: IndustryState, sq: number): number =>
  I.cooking.get(sq)?.[0] ?? 0;
export const isCooking = (I: IndustryState, sq: number): boolean =>
  (I.cooking.get(sq)?.length ?? 0) > 0;
/** How much is in the oven. A king at tempo has more than one batch going. */
export const cookingCount = (I: IndustryState, sq: number): number =>
  I.cooking.get(sq)?.length ?? 0;
export const cookingUnitAt = (I: IndustryState, sq: number, i: number): number =>
  I.cooking.get(sq)?.[i] ?? 0;

/** Put a batch at the back of the queue. */
export function pushCooking(I: IndustryState, sq: number, unit: number): void {
  const queue = I.cooking.get(sq);
  if (queue) queue.push(unit);
  else I.cooking.set(sq, [unit]);
}

/**
 * Take the oldest batch out, or -1 when the oven is empty.
 *
 * A queue rather than a single slot because a king's tempo lets it work on
 * several batches at once, and because the front/back split is what keeps the
 * one-turn cook honest at any capacity: the turn empties from the front
 * before it fills the back, so a unit accepted this turn is always behind
 * everything the turn is allowed to emit.
 */
export function shiftCooking(I: IndustryState, sq: number): number {
  const queue = I.cooking.get(sq);
  if (!queue || queue.length === 0) return -1;
  const unit = queue.shift()!;
  if (queue.length === 0) I.cooking.delete(sq);
  return unit;
}

/** Replace the whole queue — one batch, or none. For tests and for spills. */
export function setCooking(I: IndustryState, sq: number, unit: number | null): void {
  if (unit === null) I.cooking.delete(sq);
  else I.cooking.set(sq, [unit]);
}

export const targetAt = (I: IndustryState, sq: number): number => I.target.get(sq) ?? 0;
export function setTarget(I: IndustryState, sq: number, type: number): void {
  if (type) I.target.set(sq, type);
  else I.target.delete(sq);
}

export const mintFacingAt = (I: IndustryState, sq: number): number =>
  I.mintFacing.get(sq) ?? 0;
export function setMintFacing(I: IndustryState, sq: number, facing: number): void {
  if (facing) I.mintFacing.set(sq, facing);
  else I.mintFacing.delete(sq);
}

/**
 * What this foundry was told to point its pawns at, or nothing.
 *
 * **Stored one higher than the direction it means**, because `NORTH` is 0 and
 * a map that deletes on a falsy value cannot tell "face north" from "nobody
 * said". Every reader has to undo that, and doing it inline is how one of them
 * ends up off by one — so the decode lives here, beside the encode.
 *
 * `undefined` rather than null, so a caller can hand it straight to a default
 * with `??` and the two readings compose.
 */
export const mintFacingOf = (I: IndustryState, sq: number): Direction | undefined => {
  const stored = mintFacingAt(I, sq);
  return stored ? ((stored - 1) as Direction) : undefined;
};

export const refinerIdAt = (I: IndustryState, sq: number): number =>
  I.refinerId.get(sq) ?? 0;
export function setRefinerId(I: IndustryState, sq: number, id: number): void {
  if (id) I.refinerId.set(sq, id);
  else I.refinerId.delete(sq);
}

export const handoffAt = (I: IndustryState, sq: number): number =>
  I.handoff.get(sq) ?? 0;
export function setHandoff(I: IndustryState, sq: number, facing: number): void {
  if (facing) I.handoff.set(sq, facing);
  else I.handoff.delete(sq);
}

/** Is this piece told to push material it cannot use back into the network? */
export const dumpsAt = (I: IndustryState, sq: number): boolean =>
  (I.dump.get(sq) ?? 0) === 1;

/** Set the policy explicitly. Never deletes: "told to pass on" is a state. */
export function setDump(I: IndustryState, sq: number, on: boolean): void {
  if (on) I.dump.set(sq, 1);
  else I.dump.delete(sq);
}

/** Back to the type's default, for a square a piece has just left. */
export function clearDump(I: IndustryState, sq: number): void {
  I.dump.delete(sq);
}

/** The king's chosen job, or -1 when it has not been given one. */
export const kingTaskAt = (I: IndustryState, sq: number): number =>
  (I.kingTask.get(sq) ?? 0) - 1;
export function setKingTask(I: IndustryState, sq: number, task: number): void {
  if (task >= 0) I.kingTask.set(sq, task + 1);
  else I.kingTask.delete(sq);
}

/** Turns spent on the current job, and so this turn's capacity. Never below 1. */
// **The Matriarch left this file**: she is a `PIECE_VARIANTS` row on the
// position now (`isMatriarch` in `position.ts`), so there is no map, no
// accessors and no line in `migratePiece` — the variant field carries her
// through every door that moves a piece, which is what the map was for.

export const kingTempoAt = (I: IndustryState, sq: number): number =>
  I.kingTempo.get(sq) ?? 1;
export function setKingTempo(I: IndustryState, sq: number, tempo: number): void {
  if (tempo > 1) I.kingTempo.set(sq, tempo);
  else I.kingTempo.delete(sq);
}

export const kingActedAtOn = (I: IndustryState, sq: number): number =>
  I.kingActedAt.get(sq) ?? -1;
/**
 * Has this king spent its tick on something other than forging (§31 T4)?
 *
 * A king does one job a tick, so a king that dug or refined may not forge — but
 * a king whose job this tick *is* forging may forge again: Factory Flow lets a
 * side forge as many pieces as it can pay for.
 */
export const kingBusyForForge = (I: IndustryState, sq: number): boolean =>
  kingActedAtOn(I, sq) === I.turnCounter && I.kingLastTask.get(sq) !== TASK_FORGE;
export function setKingActedAt(I: IndustryState, sq: number, turn: number): void {
  if (turn >= 0) I.kingActedAt.set(sq, turn);
  else I.kingActedAt.delete(sq);
}

/** Artifacts on the ground here, oldest first. Empty for ordinary squares. */
export const droppedAt = (I: IndustryState, sq: number): readonly number[] =>
  I.dropped.get(sq) ?? EMPTY_UNITS;

/**
 * Put a unit on the ground, where anything ending its move there picks it up.
 *
 * The ordinary unit word: lifting a lane drops its ingots here, and so does
 * an altar with no room.
 */
export function dropOnGround(I: IndustryState, sq: number, unit: number): void {
  const here = I.dropped.get(sq);
  if (here) here.push(unit);
  else I.dropped.set(sq, [unit]);
}

// --- structures -----------------------------------------------------------

export const structureAt = (I: IndustryState, sq: number): Structure | undefined =>
  I.structures.get(sq);

export const hasStructure = (I: IndustryState, sq: number): boolean =>
  I.structures.has(sq);

/** Put a building on a square, or take one off with `null`. */
export function setStructure(
  I: IndustryState,
  sq: number,
  structure: Structure | null
): void {
  if (structure) I.structures.set(sq, structure);
  else I.structures.delete(sq);
}

/** Every building, in square order — never Map order (PLAN §5.5). */
export const structureSquares = (I: IndustryState): number[] =>
  [...I.structures.keys()].sort((a, b) => a - b);

/**
 * How many structures of one kind stand under one side's control (§26 A2).
 *
 * The Titan Relic's count is this question — derived from the board, so a
 * capture ends the grant with the structure and nothing has to fire an
 * event. Iteration order cannot matter to a count, so this reads the Map
 * directly rather than paying `structureSquares`'s sort.
 */
export function countStructures(I: IndustryState, kind: StructureKind, owner: Color): number {
  let n = 0;
  for (const structure of I.structures.values()) {
    if (structure.kind === kind && structure.owner === owner) n++;
  }
  return n;
}

export const stockCount = (I: IndustryState, sq: number): number =>
  I.structures.get(sq)?.stock.length ?? 0;

/** Append a unit. False when the store is full, which callers must respect. */
export function pushStock(I: IndustryState, sq: number, unit: number): boolean {
  const structure = I.structures.get(sq);
  if (!structure || structure.stock.length >= inventoryOf(structure.kind)) return false;
  structure.stock.push(unit);
  return true;
}

/** Take a unit out by index, keeping the rest in order. */
export function takeStock(I: IndustryState, sq: number, index: number): number {
  const structure = I.structures.get(sq);
  if (!structure || index < 0 || index >= structure.stock.length) return 0;
  const [unit] = structure.stock.splice(index, 1);
  return unit;
}

/**
 * Put a unit into whatever is standing on this square — a piece's inbox, a
 * building's stock, or a blueprint, which spends it and becomes a belt.
 *
 * One function, because every hand-off site in the game asks the same question
 * and none of them should have to know that a warehouse keeps its material
 * somewhere else. Returns false when there is no room, which every caller
 * already has to handle.
 *
 * **The piece wins where both are there**, which is why this needs the
 * position at all. A blueprint cannot be *drawn* under a piece, but a piece
 * can walk onto one that was already there — and at that moment the square has
 * an inbox and a recipe, both of which would take the unit. The piece is what
 * a hand-off was routed to (`canHoldUnit` answers for it), so it takes it; the
 * blueprint waits for the piece to move on, which is the same rule that stops
 * one being drawn under a piece in the first place.
 */
export function deliverUnit(
  position: Position,
  I: IndustryState,
  sq: number,
  unit: number
): boolean {
  if (I.structures.has(sq)) return pushStock(I, sq, unit);
  return pushUnit(I, sq, unit, "in");
}

/** Take the oldest unit off a square, or -1 if there is none. */
export function takeFromGround(I: IndustryState, sq: number): number {
  const here = I.dropped.get(sq);
  if (!here || here.length === 0) return -1;
  const unit = here.shift()!;
  if (here.length === 0) I.dropped.delete(sq);
  return unit;
}

/**
 * Take the cheapest unit lying on `sq` that meets a bill row, or -1 — the
 * spend half of loose ground paying a build (§31 Q13).
 */
export function takeGroundOf(I: IndustryState, sq: number, line: MaterialLine, grade: number): number {
  const here = I.dropped.get(sq);
  if (!here) return -1;
  let best = -1;
  for (let i = 0; i < here.length; i++) {
    if (!unitMeets(here[i], line, grade)) continue;
    if (best < 0 || unitValue(here[i]) < unitValue(here[best])) best = i;
  }
  if (best < 0) return -1;
  const [unit] = here.splice(best, 1);
  if (here.length === 0) I.dropped.delete(sq);
  return unit;
}

export const minedAtOn = (I: IndustryState, sq: number): number => I.minedAt.get(sq) ?? -1;

export const sacrificedAtOn = (I: IndustryState, sq: number): number =>
  I.sacrificedAt.get(sq) ?? -1;

export function setSacrificedAt(I: IndustryState, sq: number, turn: number): void {
  if (turn >= 0) I.sacrificedAt.set(sq, turn);
  else I.sacrificedAt.delete(sq);
}
export const loadedAtOn = (I: IndustryState, sq: number): number =>
  I.loadedAt.get(sq) ?? -1;

// --- health ---------------------------------------------------------------

/** Units held back from the automatic passes here. */
export const reserveAt = (I: IndustryState, sq: number): number => I.reserve.get(sq) ?? 0;

export function setReserve(I: IndustryState, sq: number, units: number): void {
  if (units > 0) I.reserve.set(sq, Math.floor(units));
  else I.reserve.delete(sq);
}

/** The round a Confessor here last empowered somebody, or -1. */
export const preachedAtOn = (I: IndustryState, sq: number): number =>
  I.preachedAt.get(sq) ?? -1;

export function setPreachedAt(I: IndustryState, sq: number, round: number): void {
  if (round >= 0) I.preachedAt.set(sq, round);
  else I.preachedAt.delete(sq);
}

/** Fuel points the machine here holds (§31 M2). */
export const fuelAt = (I: IndustryState, sq: number): number => I.fuel.get(sq) ?? 0;

export function setFuel(I: IndustryState, sq: number, points: number): void {
  if (points > 0) I.fuel.set(sq, points);
  else I.fuel.delete(sq);
}

/**
 * Fuel points vanish at the start of their owner's turn (§31 M2) — only the
 * Industrial Battery (S6) will keep them. Ascending squares, for the order rule.
 */
export function expireFuel(
  I: IndustryState,
  ownerAt: (sq: number) => number,
  side: number
): void {
  for (const sq of [...I.fuel.keys()].sort((a, b) => a - b)) {
    if (ownerAt(sq) === side) I.fuel.delete(sq);
  }
}
export function setMinedAt(I: IndustryState, sq: number, turn: number): void {
  if (turn >= 0) I.minedAt.set(sq, turn);
  else I.minedAt.delete(sq);
}
export function setLoadedAt(I: IndustryState, sq: number, turn: number): void {
  if (turn >= 0) I.loadedAt.set(sq, turn);
  else I.loadedAt.delete(sq);
}

/**
 * Every square with something of interest on it, **sorted**.
 *
 * Sorted rather than in map order: a Map iterates in insertion order, which is
 * the order things happened to be built in, and two clients replaying one log
 * would agree on that only by luck. Board order is a property of the board
 * (PLAN §5.5). The counts here are belts and heaps — tens, not thousands — so
 * sorting per sweep is far cheaper than walking an expanded board's cells.
 */
export function liveSquares(...maps: Map<number, unknown>[]): number[] {
  const seen = new Set<number>();
  for (const map of maps) for (const sq of map.keys()) seen.add(sq);
  return [...seen].sort((a, b) => a - b);
}

export function createIndustry(): IndustryState {
  return {
    growth: new Map(),
    heap: new Map(),
    heapSide: new Map(),
    heapRank: new Map(),
    deposit: new Map(),
    depositYield: new Map(),
    depositTier: new Map(),
    depositCobble: new Map(),
    digProgress: new Map(),
    beltDir: new Map(),
    beltOwner: new Map(),
    beltOn: new Map(),
    blueprintDir: new Map(),
    blueprintOwner: new Map(),
    beltCargo: new Map(),
    buffer: new Map(),
    outBuffer: new Map(),
    cooking: new Map(),
    target: new Map(),
    mintFacing: new Map(),
    refinerId: new Map(),
    nextRefinerId: 1,
    handoff: new Map(),
    dump: new Map(),
    kingTask: new Map(),
    kingTempo: new Map(),
    reserve: new Map(),
    preachedAt: new Map(),
    fuel: new Map(),
    kingLastTask: new Map(),
    kingActedAt: new Map(),
    dropped: new Map(),
    minedAt: new Map(),
    sacrificedAt: new Map(),
    loadedAt: new Map(),
    turnCounter: 0,
    structures: new Map()
  };
}

export function cloneIndustry(src: IndustryState): IndustryState {
  return {
    reserve: new Map(src.reserve),
    preachedAt: new Map(src.preachedAt),
    fuel: new Map(src.fuel),
    growth: new Map(src.growth),
    heap: new Map(src.heap),
    heapSide: new Map(src.heapSide),
    heapRank: new Map(src.heapRank),
    deposit: new Map(src.deposit),
    depositYield: new Map(src.depositYield),
    depositTier: new Map(src.depositTier),
    depositCobble: new Map(src.depositCobble),
    digProgress: new Map(src.digProgress),
    beltDir: new Map(src.beltDir),
    beltOwner: new Map(src.beltOwner),
    beltOn: new Map(src.beltOn),
    blueprintDir: new Map(src.blueprintDir),
    blueprintOwner: new Map(src.blueprintOwner),
    beltCargo: copyUnits(src.beltCargo),
    buffer: copyUnits(src.buffer),
    outBuffer: copyUnits(src.outBuffer),
    // Deep, like the unit stores: the oven is a queue now, and a shallow copy
    // would leave two states sharing one array.
    cooking: copyUnits(src.cooking),
    target: new Map(src.target),
    mintFacing: new Map(src.mintFacing),
    refinerId: new Map(src.refinerId),
    nextRefinerId: src.nextRefinerId,
    handoff: new Map(src.handoff),
    dump: new Map(src.dump),
    kingTask: new Map(src.kingTask),
    kingTempo: new Map(src.kingTempo),
    kingLastTask: new Map(src.kingLastTask),
    kingActedAt: new Map(src.kingActedAt),
    dropped: copyUnits(src.dropped),
    minedAt: new Map(src.minedAt),
    sacrificedAt: new Map(src.sacrificedAt),
    loadedAt: new Map(src.loadedAt),
    turnCounter: src.turnCounter,
    // Deep, and deep again for the stock: the tick mutates a store's units in
    // place, so a shallow copy would leave two states sharing one warehouse.
    // This is exactly the trap `run` fell into with the first card to write
    // to it.
    structures: copyStructures(src.structures),
    // Copied only when present, so a board without either clones as it did.
    ...(src.zone ? { zone: new Map(src.zone) } : {}),
    // Immutable entries, replaced wholesale on write: sharing is safe.
    ...(src.gear ? { gear: src.gear } : {})
  };
}

const copyStructures = (src: Map<number, Structure>): Map<number, Structure> => {
  const out = new Map<number, Structure>();
  for (const [sq, structure] of src) {
    out.set(sq, {
      ...structure,
      stock: structure.stock.slice(),
      ...(structure.train ? { train: copyTrain(structure.train) } : {}),
      ...(structure.turret ? { turret: { ...structure.turret } } : {})
    });
  }
  return out;
};

/**
 * Seed the opening economy. Pawn facing is set by the position, not here —
 * it governs movement and capture, so it is chess state (board.ts).
 */
export function seedIndustry(industry: IndustryState): void {
  for (const name of RULES.seedSquares) {
    setHeap(industry, parseSquare(name), RULES.seedAmount);
  }
}

export const hasBelt = (industry: IndustryState, sq: number): boolean =>
  industry.beltDir.has(sq);

export const beltIsLive = (industry: IndustryState, sq: number, owner: Color): boolean =>
  industry.beltDir.has(sq) &&
  beltOwnerAt(industry, sq) === owner &&
  beltOnAt(industry, sq) === 1;

/**
 * A running part somebody who is not an enemy owns.
 *
 * The rule for *putting material on* a lane, as against building or lifting
 * one. An army split off from yours is a labour force: it exists to feed your
 * economy, and a force that could only load its own belts would have to be
 * given a second network to be any use — at which point it is a second economy
 * rather than help with the first.
 *
 * Deposit only. Nothing here lets an ally *take* from your lanes: that is the
 * same rule read backwards and it would be theft rather than help.
 */
export const beltAcceptsFrom = (
  isEnemy: (side: Color) => boolean,
  industry: IndustryState,
  sq: number
): boolean => {
  const owner = beltOwnerAt(industry, sq);
  return (
    industry.beltDir.has(sq) && owner >= 0 && !isEnemy(owner) && beltOnAt(industry, sq) === 1
  );
};

/**
 * Square this belt feeds into, or -1 when it points at nothing.
 *
 * **`hasCell`, not `inBounds`.** A bounding-box test was right while the only
 * thing that made a board ragged also moved its edges; a structure's square
 * leaves `cells` without moving them, so holes now exist *inside* the box —
 * and a belt pointing into one was answering with a square no piece can stand
 * on and no machine can be. Harmless at the single call site this had when it
 * was found (it compares the answer against a real square either way), and
 * exactly the sort of thing that is one caller away from being live: Black
 * Hole and the Space King both make holes on purpose.
 */
export function beltTarget(
  position: Position,
  industry: IndustryState,
  sq: number
): number {
  const dir = beltDirAt(industry, sq);
  if (dir === NO_BELT) return -1;
  const next = sq + DIR_OFFSET[dir];
  return hasCell(position, next) ? next : -1;
}

/** A belt at `from` is inbound to `to` when it points straight into it. */
export const beltFeeds = (
  position: Position,
  industry: IndustryState,
  from: number,
  to: number
): boolean => beltTarget(position, industry, from) === to;

// --- material ------------------------------------------------------------
//
// Material is *units*, not counts. Each unit remembers which refineries have
// already processed it, because a refinery may never take the same unit twice
// — refine at A, then at B, and A can no longer touch it. That rule needs
// per-unit identity, which a per-grade tally cannot carry.
//
// Still flat typed arrays: cloning stays a handful of copies and iteration
// stays index-ordered, which is what keeps two networked clients in step
// (PLAN §5.5). A unit packs into one Uint32 — **grade in the low nibble, the
// line in the two bits above it, the refinery bitmask above that** (§26 F2).
// The line bits are new and the layout was rebuilt rather than kept
// compatible (decided, 2026-08-21): old saves are not worth contorting the
// encoding for. Two consequences worth knowing:
//
//  - Grade nibble 15 was the artifact marker, retired with artifact units
//    (§31 R1). Every unit is material now.
//  - The provenance mask lost two bits, so `MAX_REFINERS` fell 28 → 26 —
//    a soft cap ("beyond this it stops biting"), not an invariant.

/**
 * Units one square can hold in one store.
 *
 * **Derived, not chosen.** It has to be at least the largest buffer any piece
 * has, and it used to be a hand-picked 24 with a comment saying so — which is
 * exactly the kind of invariant that holds until somebody tunes a number in
 * another file. Raising the king's and the rook's hoppers past it silently
 * capped them: `addToBuffer` filled to 24 and stopped, so a "full" king was
 * never full, never switched off mining, and never refined. The symptom was a
 * king that mined forever.
 */
export const MAX_UNITS = Math.max(
  RULES.pawnBuffer,
  RULES.refineryInputBuffer,
  RULES.refineryOutputBuffer,
  RULES.kingBuffer,
  RULES.foundryBuffer,
  RULES.queenBuffer,
  // Derived from the buffers rather than chosen: a hand-picked cap silently
  // stops filling one that is tuned past it.
  RULES.carrierBuffer
);

/** Refineries trackable by the provenance mask. Beyond this it stops biting. */
export const MAX_REFINERS = 26;

// The unit word — `makeUnit`, `unitGrade`, `unitLine`, `unitTouched` — is
// `src/shared/industry.ts`, re-exported here.
export { makeUnit, unitGrade, unitLine, unitTouched };

export const unitTouchedBy = (unit: number, refinerId: number): boolean =>
  refinerId > 0 &&
  refinerId <= MAX_REFINERS &&
  (unitTouched(unit) & (1 << (refinerId - 1))) !== 0;

/**
 * The same unit, one grade higher and stamped with the refinery that did it.
 * The line rides along: cooking raises a rung, it never changes what the
 * material *is*.
 */
export function unitRefinedBy(unit: number, refinerId: number, grade: number): number {
  const mark =
    refinerId > 0 && refinerId <= MAX_REFINERS ? 1 << (refinerId - 1) : 0;
  return makeUnit(grade, unitTouched(unit) | mark, unitLine(unit));
}

/**
 * Does this unit satisfy a bill row of (`line`, `grade`)? The one reading. On a
 * **ladder** a higher rung pays for a lower (crucible for steel); a
 * **resource** row (electrum, diamond, coal) takes that resource and nothing
 * else (§31 M1). Never across lines.
 */
export const unitMeets = (unit: number, line: MaterialLine, grade: number): boolean =>
  unitLine(unit) === line &&
  (isLadder(line) ? unitGrade(unit) >= grade : unitGrade(unit) === grade);

/**
 * What this unit is worth, cross-line, off `RULES.materialValue` — the one
 * price every "take the best, spend the worst" rule compares by now that a
 * raw grade cannot rank marble against steel.
 */
export const unitValue = (unit: number): number =>
  materialValue(unitLine(unit), unitGrade(unit));

export type Store = "cargo" | "in" | "out";

const storeOf = (industry: IndustryState, store: Store): Map<number, number[]> =>
  store === "cargo"
    ? industry.beltCargo
    : store === "in"
    ? industry.buffer
    : industry.outBuffer;

/** The units on a square, or a shared empty array — never mutate the result. */
const unitsAt = (industry: IndustryState, sq: number, store: Store): number[] =>
  storeOf(industry, store).get(sq) ?? EMPTY_UNITS;

const EMPTY_UNITS: number[] = [];

export const unitCount = (industry: IndustryState, sq: number, store: Store): number =>
  unitsAt(industry, sq, store).length;

export const unitAt = (
  industry: IndustryState,
  sq: number,
  index: number,
  store: Store
): number => unitsAt(industry, sq, store)[index] ?? 0;

/** Append a unit. Returns false when the square is full, which callers must respect. */
export function pushUnit(
  industry: IndustryState,
  sq: number,
  unit: number,
  store: Store
): boolean {
  const map = storeOf(industry, store);
  let units = map.get(sq);
  if (units === undefined) {
    units = [];
    map.set(sq, units);
  }
  // A belt part holds `RULES.beltCapacity` (§33 I3); a bank `MAX_UNITS`.
  if (units.length >= (store === "cargo" ? RULES.beltCapacity : MAX_UNITS)) {
    if (units.length === 0) map.delete(sq);
    return false;
  }
  units.push(unit);
  return true;
}

/** Remove by index, keeping the remaining order stable. */
export function removeUnit(
  industry: IndustryState,
  sq: number,
  index: number,
  store: Store
): number {
  const map = storeOf(industry, store);
  const units = map.get(sq);
  if (units === undefined || index >= units.length) return 0;
  const [unit] = units.splice(index, 1);
  if (units.length === 0) map.delete(sq);
  return unit;
}

export function clearStore(industry: IndustryState, sq: number, store: Store): number {
  const map = storeOf(industry, store);
  const removed = map.get(sq)?.length ?? 0;
  map.delete(sq);
  return removed;
}

// --- belt cargo -----------------------------------------------------------

export const cargoTotal = (industry: IndustryState, sq: number): number =>
  unitCount(industry, sq, "cargo");

export const hasCargo = (industry: IndustryState, sq: number): boolean =>
  cargoTotal(industry, sq) > 0;

export function cargoAt(industry: IndustryState, sq: number, grade: number): number {
  let count = 0;
  for (let i = 0; i < cargoTotal(industry, sq); i++) {
    if (unitGrade(unitAt(industry, sq, i, "cargo")) === grade) count++;
  }
  return count;
}

export function addCargo(
  industry: IndustryState,
  sq: number,
  grade: number,
  amount = 1,
  line: MaterialLine = METAL
): void {
  for (let i = 0; i < amount; i++) pushUnit(industry, sq, makeUnit(grade, 0, line), "cargo");
}

/** Move a whole pile onto `to`, adding to anything already there. */
export function mergeCargo(industry: IndustryState, from: number, to: number): void {
  const moving = cargoTotal(industry, from);
  for (let i = 0; i < moving; i++) {
    pushUnit(industry, to, unitAt(industry, from, i, "cargo"), "cargo");
  }
  clearStore(industry, from, "cargo");
}

export const clearCargo = (industry: IndustryState, sq: number): number =>
  clearStore(industry, sq, "cargo");

/** Highest grade present on a square, or -1 when it is empty. */
export function topCargoGrade(industry: IndustryState, sq: number): number {
  let best = -1;
  for (let i = 0; i < cargoTotal(industry, sq); i++) {
    const grade = unitGrade(unitAt(industry, sq, i, "cargo"));
    if (grade > best) best = grade;
  }
  return best;
}

// --- machine buffers ------------------------------------------------------

/** Which side of a machine a helper is addressing. Refineries have both. */
export type Bank = "in" | "out";

export const bufferTotal = (
  industry: IndustryState,
  sq: number,
  side: Bank = "in"
): number => unitCount(industry, sq, side);

export function bufferAt(
  industry: IndustryState,
  sq: number,
  grade: Grade,
  side: Bank = "in"
): number {
  let count = 0;
  for (let i = 0; i < bufferTotal(industry, sq, side); i++) {
    if (unitGrade(unitAt(industry, sq, i, side)) === grade) count++;
  }
  return count;
}

export function addToBuffer(
  industry: IndustryState,
  sq: number,
  grade: Grade,
  amount = 1,
  side: Bank = "in",
  line: MaterialLine = METAL
): void {
  for (let i = 0; i < amount; i++) pushUnit(industry, sq, makeUnit(grade, 0, line), side);
}

/**
 * Take one unit of at least `minGrade` — any line — cheapest **by value**
 * first: spend the cheap material before the expensive stuff, and now that
 * grades live in lines, "cheap" is the cross-line price table rather than the
 * rung. Returns the packed unit, or -1, so the caller keeps its provenance.
 *
 * Line-agnostic on purpose, for the callers whose spend genuinely is — a
 * a whole-bank spend eats anything. A bill row
 * spends `takeUnitOf` instead.
 */
export function takeUnit(
  industry: IndustryState,
  sq: number,
  minGrade: Grade = 0,
  side: Bank = "in"
): number {
  let bestIndex = -1;
  let bestValue = Infinity;
  for (let i = 0; i < bufferTotal(industry, sq, side); i++) {
    const unit = unitAt(industry, sq, i, side);
    if (unitGrade(unit) < minGrade) continue;
    const value = unitValue(unit);
    if (value < bestValue) {
      bestValue = value;
      bestIndex = i;
    }
  }
  return bestIndex < 0 ? -1 : removeUnit(industry, sq, bestIndex, side);
}

/**
 * Take one unit that satisfies a bill row — `line`, at `minGrade` or better —
 * cheapest by value first. The spend half of `unitMeets`.
 */
export function takeUnitOf(
  industry: IndustryState,
  sq: number,
  line: MaterialLine,
  minGrade: number,
  side: Bank = "in"
): number {
  let bestIndex = -1;
  let bestValue = Infinity;
  for (let i = 0; i < bufferTotal(industry, sq, side); i++) {
    const unit = unitAt(industry, sq, i, side);
    if (!unitMeets(unit, line, minGrade)) continue;
    const value = unitValue(unit);
    if (value < bestValue) {
      bestValue = value;
      bestIndex = i;
    }
  }
  return bestIndex < 0 ? -1 : removeUnit(industry, sq, bestIndex, side);
}

/** As `takeUnit`, but for callers that only care about the grade. */
export function takeFromBuffer(
  industry: IndustryState,
  sq: number,
  minGrade: Grade = 0,
  side: Bank = "in"
): Grade | -1 {
  const unit = takeUnit(industry, sq, minGrade, side);
  return unit < 0 ? -1 : (unitGrade(unit) as Grade);
}

/**
 * Units of at least `minGrade` held here. Any line when `line` is omitted —
 * the reading for "how much of anything is this holding" — and one line's
 * worth when it is given, which is what a bill row counts.
 */
export function bufferAtLeast(
  industry: IndustryState,
  sq: number,
  minGrade: Grade,
  side: Bank = "in",
  line?: MaterialLine
): number {
  let total = 0;
  for (let i = 0; i < bufferTotal(industry, sq, side); i++) {
    const unit = unitAt(industry, sq, i, side);
    // A line given is a bill row, so it counts the way a row is paid.
    if (line !== undefined ? unitMeets(unit, line, minGrade) : unitGrade(unit) >= minGrade) total++;
  }
  return total;
}

/** Empty both banks and return how much was in them. */
export function clearBuffer(industry: IndustryState, sq: number): number {
  return clearStore(industry, sq, "in") + clearStore(industry, sq, "out");
}

/**
 * Move everything a piece carries from one square to another. Buffers, the
 * refinery oven, the foundry target and pawn facing all belong to the *piece*,
 * not the square, so every move has to carry them along.
 */
export function migratePiece(industry: IndustryState, from: number, to: number): void {
  for (const store of ["in", "out"] as const) {
    clearStore(industry, to, store);
    const moving = unitCount(industry, from, store);
    for (let i = 0; i < moving; i++) {
      pushUnit(industry, to, unitAt(industry, from, i, store), store);
    }
    clearStore(industry, from, store);
  }
  setRefinerId(industry, to, refinerIdAt(industry, from));
  setRefinerId(industry, from, 0);
  setMinedAt(industry, to, minedAtOn(industry, from));
  setMinedAt(industry, from, -1);
  setLoadedAt(industry, to, loadedAtOn(industry, from));
  setLoadedAt(industry, from, -1);
  // **Health used to travel from here and is `Position`'s now** (§27 H1) — it
  // has to be where make/unmake can put it back. What still travels is the
  // repair kit a knight is holding back and whether it has already repaired
  // this turn: a knight that could step sideways and bandage again would get
  // two turns' work out of one.
  // The costume travels through the `Undo` word rather than here (§26 C12).
  setReserve(industry, to, reserveAt(industry, from));
  setReserve(industry, from, 0);
  // Hers, not her square's — she may walk and still be on cooldown.
  setPreachedAt(industry, to, preachedAtOn(industry, from));
  setPreachedAt(industry, from, -1);
  // A refiner's fuel points are its own, made for its next cook.
  setFuel(industry, to, fuelAt(industry, from));
  setFuel(industry, from, 0);
  const oven = industry.cooking.get(from);
  if (oven) industry.cooking.set(to, oven.slice());
  else industry.cooking.delete(to);
  industry.cooking.delete(from);
  setTarget(industry, to, targetAt(industry, from));
  setTarget(industry, from, 0);
  // The crown is not here any more: a Matriarch is a variant row, so she
  // travels the way every row does — `Undo.variantFrom`, `shovePiece`,
  // `placeStep` — and make/unmake can put her back.
  setMintFacing(industry, to, mintFacingAt(industry, from));
  setMintFacing(industry, from, 0);
  setHandoff(industry, to, handoffAt(industry, from));
  setHandoff(industry, from, 0);
  // Belongs to the piece, not the square: a foundry told to dump does not
  // start hoarding again because it stepped sideways.
  setDump(industry, to, dumpsAt(industry, from));
  clearDump(industry, from);
  // The posting travels: you told this king to work the ovens, and walking it
  // to a better square is not you changing your mind about that.
  setKingTask(industry, to, kingTaskAt(industry, from));
  setKingTask(industry, from, -1);

  // The streak does not. Tempo is what a king earns by being left alone, and
  // a king that has just marched across the board has not been left alone —
  // carrying it would have made moving free, so the piece that is hardest to
  // commit would also have been the one that paid nothing to reposition.
  setKingTempo(industry, to, 1);
  setKingTempo(industry, from, 1);
  industry.kingLastTask.delete(to);
  industry.kingLastTask.delete(from);

  // Whether it has already worked *this* turn does travel, or a king could
  // dig, move, and refine on the far side of the same turn.
  setKingActedAt(industry, to, kingActedAtOn(industry, from));
  setKingActedAt(industry, from, -1);
}

/**
 * Everything a piece is holding, as grade-0 scrap. Grade is destroyed with its
 * carrier (PLAN §3.1) — which is what puts a risk premium on long refining
 * chains, and what makes a loaded refinery a fat target.
 */
export function stripHoldings(industry: IndustryState, sq: number): number {
  let total = clearBuffer(industry, sq);
  total += cookingCount(industry, sq);
  industry.cooking.delete(sq);
  setTarget(industry, sq, 0);
  setMintFacing(industry, sq, 0);
  setRefinerId(industry, sq, 0);
  setFuel(industry, sq, 0);
  return total;
}

/** How much further the ground at this square will carry, or 0. */
export const growthAt = (I: IndustryState, sq: number): number =>
  (I.growth.size > 0 ? I.growth.get(sq) : undefined) ?? 0;

/** Set a square's growth counter, or clear it with 0. */
export function setGrowth(I: IndustryState, sq: number, left: number): void {
  if (left > 0) I.growth.set(sq, left);
  else I.growth.delete(sq);
}

/** Every square still growing, sorted. */
export const growthSquares = (I: IndustryState): number[] =>
  [...I.growth.keys()].sort((a, b) => a - b);

